import React, { useEffect, useState } from "react";
import api from "../api";

import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

import * as XLSX from "xlsx";

import "../Styles/TrialBalance.css";

import { useNavigate } from "react-router-dom";

import {
  Scale,
  ArrowLeft,
  CalendarDays,
  Wallet,
  Landmark,
  Download,
  FileSpreadsheet,
  ChevronRight,
} from "lucide-react";

const TrialBalance = () => {

  const [accounts, setAccounts] =
    useState([]);

  const [loading, setLoading] =
    useState(true);

  const now = new Date();

  const [selectedMonth, setSelectedMonth] =
    useState(now.getMonth());

  const [selectedYear, setSelectedYear] =
    useState(now.getFullYear());

  const navigate = useNavigate();

  useEffect(() => {

    const extractTrialBalance =
      async () => {

        try {

          setLoading(true);

          const accs =
            await api.getAccounts();

          const result = [];

          for (const acc of accs) {

            const txns =
              await api.getTransactions(acc.id);

            const filtered =
              txns.filter((t) => {

                const d =
                  new Date(t.date);

                return (
                  d.getFullYear() ===
                    selectedYear &&
                  d.getMonth() ===
                    selectedMonth
                );

              });

            const totalDebits =
              filtered.reduce(
                (sum, t) =>
                  sum +
                  parseFloat(
                    t.debit || 0
                  ),
                0
              );

            const totalCredits =
              filtered.reduce(
                (sum, t) =>
                  sum +
                  parseFloat(
                    t.credit || 0
                  ),
                0
              );

            const balance =
              totalDebits -
              totalCredits;

            if (balance !== 0) {

              result.push({

                name: acc.name,

                debit:
                  balance > 0
                    ? balance
                    : 0,

                credit:
                  balance < 0
                    ? Math.abs(balance)
                    : 0,

              });

            }

          }

          setAccounts(result);

        } catch (err) {

          console.error(
            "❌ Error extracting trial balance:",
            err
          );

        } finally {

          setLoading(false);

        }

      };

    extractTrialBalance();

  }, [selectedMonth, selectedYear]);

  const totalDebit =
    accounts.reduce(
      (sum, a) =>
        sum + a.debit,
      0
    );

  const totalCredit =
    accounts.reduce(
      (sum, a) =>
        sum + a.credit,
      0
    );

  /* EXPORT PDF */

  const exportPDF = () => {

    const doc = new jsPDF();

    doc.text(
      "Trial Balance",
      14,
      12
    );

    autoTable(doc, {

      startY: 22,

      head: [[
        "Account",
        "Debit",
        "Credit",
      ]],

      body:
        accounts.map(a => [

          a.name,

          a.debit
            ? `$${a.debit.toLocaleString()}`
            : "-",

          a.credit
            ? `$${a.credit.toLocaleString()}`
            : "-",

        ]),

      foot: [[
        "Total",
        `$${totalDebit.toLocaleString()}`,
        `$${totalCredit.toLocaleString()}`,
      ]],

    });

    doc.save(
      "TrialBalance.pdf"
    );

  };

  /* EXPORT EXCEL */

  const exportExcel = () => {

    const worksheet =
      XLSX.utils.json_to_sheet(

        accounts.map(a => ({

          Account: a.name,

          Debit: a.debit,

          Credit: a.credit,

        }))

      );

    const workbook =
      XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(
      workbook,
      worksheet,
      "Trial Balance"
    );

    XLSX.writeFile(
      workbook,
      "TrialBalance.xlsx"
    );

  };

  if (loading) {

    return (

      <div className="trial-page">

        <div className="trial-loading-card">

          <div className="trial-spinner"></div>

          <p>
            Loading Trial Balance...
          </p>

        </div>

      </div>

    );

  }

  return (

    <div className="trial-page">

      {/* HERO */}

      <div className="trial-hero">

        <div className="trial-hero-left">

          <div className="trial-pill">

            <Scale size={15} />

            <span>
              Trial Balance
            </span>

          </div>

          <h1>
            Financial
            <br />
            Balancing
          </h1>

          <p>
            Review account balances,
            verify debit and credit equality
            and prepare finalized balances
            for reporting and reconciliation.
          </p>

          <div className="trial-actions">

            <button
              className="trial-btn primary"
              onClick={() =>
                navigate("/journals")
              }
            >
              Open Journals
              <ChevronRight size={17} />
            </button>

            <button
              className="trial-btn secondary"
              onClick={() =>
                navigate("/accounts")
              }
            >
              <ArrowLeft size={16} />
              Accounts
            </button>

          </div>

        </div>

        {/* RIGHT */}

        <div className="trial-summary-grid">

          <div className="summary-card green">

            <div className="summary-icon">
              <Wallet size={28} />
            </div>

            <div>

              <span>Total Debits</span>

              <h2>
                $
                {totalDebit.toLocaleString()}
              </h2>

            </div>

          </div>

          <div className="summary-card red">

            <div className="summary-icon">
              <Landmark size={28} />
            </div>

            <div>

              <span>Total Credits</span>

              <h2>
                $
                {totalCredit.toLocaleString()}
              </h2>

            </div>

          </div>

          <div className="summary-card blue">

            <div className="summary-icon">
              <CalendarDays size={28} />
            </div>

            <div>

              <span>Selected Period</span>

              <h2>
                {new Date(
                  selectedYear,
                  selectedMonth
                ).toLocaleString(
                  "en-US",
                  {
                    month: "long",
                    year: "numeric",
                  }
                )}
              </h2>

            </div>

          </div>

        </div>

      </div>

      {/* FILTER BAR */}

      <div className="trial-filter-bar">

        <div className="filter-group">

          <label>Month</label>

          <select
            value={selectedMonth}
            onChange={(e) =>
              setSelectedMonth(
                parseInt(e.target.value)
              )
            }
          >

            {Array.from(
              { length: 12 },
              (_, i) => (

                <option
                  key={i}
                  value={i}
                >
                  {new Date(
                    0,
                    i
                  ).toLocaleString(
                    "en-US",
                    {
                      month: "long",
                    }
                  )}
                </option>

              )
            )}

          </select>

        </div>

        <div className="filter-group">

          <label>Year</label>

          <select
            value={selectedYear}
            onChange={(e) =>
              setSelectedYear(
                parseInt(e.target.value)
              )
            }
          >

            {Array.from(
              { length: 5 },
              (_, i) => {

                const year =
                  now.getFullYear() - 2 + i;

                return (

                  <option
                    key={year}
                    value={year}
                  >
                    {year}
                  </option>

                );

              }
            )}

          </select>

        </div>

        {/* EXPORTS */}

        <div className="trial-export-group">

          <button
            className="export-btn pdf"
            onClick={exportPDF}
          >
            <Download size={16} />
            PDF
          </button>

          <button
            className="export-btn excel"
            onClick={exportExcel}
          >
            <FileSpreadsheet size={16} />
            Excel
          </button>

        </div>

      </div>

      {/* TABLE */}

      <div className="trial-table-wrapper">

        <table className="trial-table">

          <thead>

            <tr>
              <th>Account</th>
              <th>Debit</th>
              <th>Credit</th>
            </tr>

          </thead>

          <tbody>

            {accounts.map((a, i) => (

              <tr key={i}>

                <td className="account-name">
                  {a.name}
                </td>

                <td className="debit-amount">
                  {a.debit
                    ? `$${a.debit.toLocaleString()}`
                    : "-"}
                </td>

                <td className="credit-amount">
                  {a.credit
                    ? `$${a.credit.toLocaleString()}`
                    : "-"}
                </td>

              </tr>

            ))}

            <tr className="total-row">

              <td>
                Totals
              </td>

              <td>
                $
                {totalDebit.toLocaleString()}
              </td>

              <td>
                $
                {totalCredit.toLocaleString()}
              </td>

            </tr>

          </tbody>

        </table>

      </div>

    </div>

  );

};

export default TrialBalance;